import type {
  AccountAnalyticsRow,
  AnalyticsTotals,
  BuyerAnalyticsRow,
} from "@/api/hooks/dashboard"

/**
 * Client-side totals recomputation used when a search filter is
 * active (parity: `dashboard.html:1584-1621` — `_computeTotalsFromBuyerRows`
 * and `_computeTotalsFromAccountRows`).
 *
 * Formulas (shared between both tables; the only difference is the
 * row shape consumed):
 *
 *   profit = revenue - cost
 *   cpc    = cost / clicks        (0 when clicks == 0)
 *   cpm    = cost / impressions * 1000  (0 when impressions == 0)
 *   cpa    = cost / gclicks, falling back to cost / conversions,
 *            else 0
 *   rpc    = revenue / gclicks, falling back to revenue / clicks,
 *            else 0
 *   roi    = (revenue - cost) / cost * 100    (null when cost == 0)
 *   cvr    = gclicks / clicks * 100           (null when clicks == 0)
 *
 * Parity note: `roi` and `cvr` return `null` (not 0) when their
 * denominator is zero so the `-` formatter shows in the UI. Every
 * other derived field returns `0` in that case.
 */

function num(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

interface RawRow {
  cost?: number | null
  revenue?: number | null
  impressions?: number | null
  clicks?: number | null
  conversions?: number | null
  gclicks?: number | null
  balance?: number | null
  active_campaigns?: number | null
  accounts_active?: number | null
  accounts_total?: number | null
}

function computeTotals(rows: RawRow[]): AnalyticsTotals {
  let cost = 0
  let revenue = 0
  let impressions = 0
  let clicks = 0
  let conversions = 0
  let gclicks = 0
  let balance = 0
  let active_campaigns = 0
  let accounts_active = 0
  let accounts_total = 0
  for (const r of rows) {
    cost += num(r.cost)
    revenue += num(r.revenue)
    impressions += num(r.impressions)
    clicks += num(r.clicks)
    conversions += num(r.conversions)
    gclicks += num(r.gclicks)
    balance += num(r.balance)
    active_campaigns += num(r.active_campaigns)
    accounts_active += num(r.accounts_active)
    accounts_total += num(r.accounts_total)
  }
  const profit = revenue - cost
  const cpc = clicks > 0 ? cost / clicks : 0
  const cpm = impressions > 0 ? (cost / impressions) * 1000 : 0
  const cpa =
    gclicks > 0
      ? cost / gclicks
      : conversions > 0
        ? cost / conversions
        : 0
  const rpc =
    gclicks > 0
      ? revenue / gclicks
      : clicks > 0
        ? revenue / clicks
        : 0
  const roi = cost > 0 ? ((revenue - cost) / cost) * 100 : null
  const cvr = clicks > 0 ? (gclicks / clicks) * 100 : null
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : null
  return {
    cost,
    revenue,
    profit,
    impressions,
    clicks,
    conversions,
    gclicks,
    cpc,
    cpm,
    cpa,
    rpc,
    roi,
    cvr,
    ctr,
    balance,
    active_campaigns,
    accounts_active,
    accounts_total,
  }
}

export function _computeTotalsFromBuyerRows(
  rows: BuyerAnalyticsRow[],
): AnalyticsTotals {
  return computeTotals(rows)
}

export function _computeTotalsFromAccountRows(
  rows: AccountAnalyticsRow[],
): AnalyticsTotals {
  return computeTotals(rows)
}
